import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getAllUsers, deleteUser, User } from '../services/auth';
import { getApiUrl } from '../config/domains';

const AdminPanel: React.FC = () => {
  const { user, isAdmin, isUserAdmin, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Helper function to get auth token from localStorage
  const getAuthToken = () => {
    return localStorage.getItem('intrnauts_auth_token');
  };

  // Helper function to create auth headers
  const getAuthHeaders = () => {
    const token = getAuthToken();
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
    };
  };

  // Redirect if not authenticated or not admin/user_admin
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: { pathname: '/admin' } } });
    } else if (!isAdmin && !isUserAdmin) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, isAdmin, isUserAdmin, navigate]);

  // Fetch users
  useEffect(() => {
    const fetchUsers = async () => {
      if (isAdmin || isUserAdmin) {
        setLoading(true);
        const response = await getAllUsers();
        setLoading(false);

        if (response.error) {
          setError(response.error);
        } else if (response.data) {
          setUsers(response.data);
        }
      }
    };

    fetchUsers();
  }, [isAdmin, isUserAdmin]);

  const handleDeleteUser = async (userId: number) => {
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }

    const response = await deleteUser(userId);

    if (response.error) {
      setError(response.error);
      setMessage('');
    } else {
      setMessage('User deleted successfully');
      setError('');
      // Refresh user list
      setUsers(users.filter(u => u.id !== userId));
    }
  };

  const handleAssignBloggerRole = async (email: string) => {
    try {
      const API_URL = getApiUrl();
      const response = await fetch(`${API_URL}/admin/assign-blogger-role?email=${encodeURIComponent(email)}`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to assign blogger role');
      }

      setMessage('Blogger role assigned successfully');
      setError('');

      // Refresh the user list
      const usersResponse = await getAllUsers();
      if (usersResponse.data) {
        setUsers(usersResponse.data);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to assign blogger role');
      setMessage('');
    }
  };

  const handleRemoveBloggerRole = async (email: string) => {
    try {
      const API_URL = getApiUrl();
      const response = await fetch(`${API_URL}/admin/remove-blogger-role?email=${encodeURIComponent(email)}`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to remove blogger role');
      }

      setMessage('Blogger role removed successfully');
      setError('');

      // Refresh the user list
      const usersResponse = await getAllUsers();
      if (usersResponse.data) {
        setUsers(usersResponse.data);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove blogger role');
      setMessage('');
    }
  };

  const handleAssignUserAdminRole = async (email: string) => {
    try {
      const API_URL = getApiUrl();
      const response = await fetch(`${API_URL}/admin/assign-user-admin-role?email=${encodeURIComponent(email)}`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to assign user_admin role');
      }

      setMessage('User Admin role assigned successfully');
      setError('');

      // Refresh the user list
      const usersResponse = await getAllUsers();
      if (usersResponse.data) {
        setUsers(usersResponse.data);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to assign user_admin role');
      setMessage('');
    }
  };

  const handleRemoveUserAdminRole = async (email: string) => {
    try {
      const API_URL = getApiUrl();
      const response = await fetch(`${API_URL}/admin/remove-user-admin-role?email=${encodeURIComponent(email)}`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to remove user_admin role');
      }

      setMessage('User Admin role removed successfully');
      setError('');

      // Refresh the user list
      const usersResponse = await getAllUsers();
      if (usersResponse.data) {
        setUsers(usersResponse.data);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove user_admin role');
      setMessage('');
    }
  };

  const handleMakeSuperuser = async (email: string) => {
    // Extra confirmation for this powerful action
    if (!window.confirm(
      `⚠️ WARNING: This will grant FULL administrative privileges to ${email}.\n\n` +
      `Superusers can:\n` +
      `• Manage all users and roles\n` +
      `• Delete other superusers\n` +
      `• Access all system features\n\n` +
      `Are you absolutely sure you want to proceed?`
    )) {
      return;
    }

    try {
      const API_URL = getApiUrl();
      const response = await fetch(`${API_URL}/admin/make-superuser?email=${encodeURIComponent(email)}`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to make user a superuser');
      }

      const data = await response.json();
      setMessage(data.message || 'User is now a superuser');
      setError('');

      // Refresh the user list
      const usersResponse = await getAllUsers();
      if (usersResponse.data) {
        setUsers(usersResponse.data);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to make user a superuser');
      setMessage('');
    }
  };

  const handleDemoteSuperuser = async (email: string) => {
    if (!window.confirm(
      `Are you sure you want to demote ${email} from superuser to user_admin?\n\n` +
      `They will retain user_admin privileges but lose superuser access.`
    )) {
      return;
    }

    try {
      const API_URL = getApiUrl();
      const response = await fetch(`${API_URL}/admin/demote-superuser?email=${encodeURIComponent(email)}`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to demote superuser');
      }

      const data = await response.json();
      setMessage(data.message || 'User has been demoted to user_admin');
      setError('');

      // Refresh the user list
      const usersResponse = await getAllUsers();
      if (usersResponse.data) {
        setUsers(usersResponse.data);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to demote superuser');
      setMessage('');
    }
  };

  const handleToggleUserStatus = async (userId: number, currentStatus: string) => {
    const action = currentStatus === 'active' ? 'deactivate' : 'activate';
    if (!window.confirm(`Are you sure you want to ${action} this user?`)) {
      return;
    }

    try {
      const API_URL = getApiUrl();
      const response = await fetch(`${API_URL}/admin/toggle-user-status/${userId}`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to toggle user status');
      }

      const data = await response.json();
      setMessage(data.message || `User status updated to ${data.user.status}`);
      setError('');

      // Refresh the user list
      const usersResponse = await getAllUsers();
      if (usersResponse.data) {
        setUsers(usersResponse.data);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to toggle user status');
      setMessage('');
    }
  };

  // Filter users based on search term
  const filteredUsers = users.filter(u =>
    u.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.last_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!isAdmin && !isUserAdmin) {
    return null;
  }

  return (
    <div className="page-container">
      <div className="admin-container">
        <h1>Admin Panel</h1>
        <p>Manage users and system settings</p>

        <div style={{ marginBottom: '2rem' }}>
          <Link
            to="/admin/content"
            style={{
              display: 'inline-block',
              padding: '0.6rem 1.25rem',
              backgroundColor: '#007bff',
              color: 'white',
              borderRadius: '4px',
              textDecoration: 'none',
              fontWeight: 500,
              fontSize: '0.9rem',
            }}
          >
            Manage Site Content
          </Link>
        </div>

        {message && (
          <div className="success-message" style={{
            padding: '12px',
            backgroundColor: '#efe',
            border: '1px solid #cfc',
            borderRadius: '4px',
            marginBottom: '20px',
            color: '#060'
          }}>
            {message}
          </div>
        )}

        {error && (
          <div className="error-message" style={{
            padding: '12px',
            backgroundColor: '#fee',
            border: '1px solid #fcc',
            borderRadius: '4px',
            marginBottom: '20px',
            color: '#c00'
          }}>
            {error}
          </div>
        )}

        {/* User Management Section */}
        <div className="admin-section">
          <h2>User Management</h2>

          <div className="search-bar" style={{ marginBottom: '20px' }}>
            <input
              type="text"
              placeholder="Search users by email, username, or name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                fontSize: '16px',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
            />
          </div>

          {loading ? (
            <div>Loading users...</div>
          ) : (
            <div className="users-table">
              <p style={{ marginBottom: '15px' }}>
                Total Users: {filteredUsers.length}
              </p>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                backgroundColor: 'white',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}>
                <thead>
                  <tr style={{ backgroundColor: '#f5f5f5' }}>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>ID</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Email</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Username</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Name</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Status</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Roles</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.length === 0 ? (
                    <tr>
                      <td colSpan={7} style={{ padding: '20px', textAlign: 'center' }}>
                        No users found
                      </td>
                    </tr>
                  ) : (
                    filteredUsers.map(u => (
                      <tr key={u.id} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '12px' }}>{u.id}</td>
                        <td style={{ padding: '12px' }}>
                          {u.email}
                          {u.is_superuser && (
                            <span style={{
                              marginLeft: '8px',
                              padding: '2px 8px',
                              backgroundColor: '#f0ad4e',
                              color: 'white',
                              borderRadius: '3px',
                              fontSize: '12px'
                            }}>
                              ADMIN
                            </span>
                          )}
                          {!u.is_superuser && u.roles.includes('user_admin') && (
                            <span style={{
                              marginLeft: '8px',
                              padding: '2px 8px',
                              backgroundColor: '#17a2b8',
                              color: 'white',
                              borderRadius: '3px',
                              fontSize: '12px'
                            }}>
                              USER ADMIN
                            </span>
                          )}
                        </td>
                        <td style={{ padding: '12px' }}>{u.username || '-'}</td>
                        <td style={{ padding: '12px' }}>
                          {u.first_name || u.last_name
                            ? `${u.first_name || ''} ${u.last_name || ''}`.trim()
                            : '-'
                          }
                        </td>
                        <td style={{ padding: '12px' }}>
                          <span style={{
                            padding: '4px 8px',
                            borderRadius: '3px',
                            fontSize: '12px',
                            backgroundColor: u.status === 'active' ? '#e8f5e9' : '#ffebee',
                            color: u.status === 'active' ? '#2e7d32' : '#c62828'
                          }}>
                            {u.status}
                          </span>
                          {u.is_verified && (
                            <span style={{
                              marginLeft: '8px',
                              padding: '4px 8px',
                              backgroundColor: '#e3f2fd',
                              color: '#1976d2',
                              borderRadius: '3px',
                              fontSize: '12px'
                            }}>
                              ✓
                            </span>
                          )}
                        </td>
                        <td style={{ padding: '12px' }}>
                          {u.roles.length > 0 ? u.roles.join(', ') : '-'}
                        </td>
                        <td style={{ padding: '12px' }}>
                          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            {/* Superuser Management (only visible to superusers) */}
                            {u.is_superuser ? (
                              <>
                                {isAdmin && u.id !== user?.id && (
                                  <button
                                    onClick={() => handleDemoteSuperuser(u.email)}
                                    style={{
                                      padding: '6px 12px',
                                      backgroundColor: '#ff9800',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '4px',
                                      cursor: 'pointer',
                                      fontSize: '12px',
                                      fontWeight: 'bold',
                                      whiteSpace: 'nowrap'
                                    }}
                                    title="Demote to User Admin (keeps user_admin role)"
                                  >
                                    ↓ Demote Superuser
                                  </button>
                                )}
                              </>
                            ) : (
                              <>
                                {/* Blogger role management */}
                                {u.roles.includes('blogger') ? (
                                  <button
                                    onClick={() => handleRemoveBloggerRole(u.email)}
                                    style={{
                                      padding: '6px 12px',
                                      backgroundColor: '#ffc107',
                                      color: '#000',
                                      border: 'none',
                                      borderRadius: '4px',
                                      cursor: 'pointer',
                                      fontSize: '12px',
                                      whiteSpace: 'nowrap'
                                    }}
                                  >
                                    Remove Blogger
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => handleAssignBloggerRole(u.email)}
                                    style={{
                                      padding: '6px 12px',
                                      backgroundColor: '#28a745',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '4px',
                                      cursor: 'pointer',
                                      fontSize: '12px',
                                      whiteSpace: 'nowrap'
                                    }}
                                  >
                                    Make Blogger
                                  </button>
                                )}

                                {/* User Admin role management (only for superusers) */}
                                {isAdmin && (
                                  <>
                                    {u.roles.includes('user_admin') ? (
                                      <>
                                        <button
                                          onClick={() => handleRemoveUserAdminRole(u.email)}
                                          style={{
                                            padding: '6px 12px',
                                            backgroundColor: '#ff6b6b',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            fontSize: '12px',
                                            whiteSpace: 'nowrap'
                                          }}
                                        >
                                          Remove User Admin
                                        </button>
                                        {/* Make Superuser button (only if user is already user_admin - 2 step process) */}
                                        <button
                                          onClick={() => handleMakeSuperuser(u.email)}
                                          style={{
                                            padding: '6px 12px',
                                            backgroundColor: '#f0ad4e',
                                            color: '#000',
                                            border: '2px solid #d39e00',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            fontSize: '12px',
                                            fontWeight: 'bold',
                                            whiteSpace: 'nowrap'
                                          }}
                                          title="Grant full administrative privileges (requires user_admin first)"
                                        >
                                          ⚠️ Make Superuser
                                        </button>
                                      </>
                                    ) : (
                                      <button
                                        onClick={() => handleAssignUserAdminRole(u.email)}
                                        style={{
                                          padding: '6px 12px',
                                          backgroundColor: '#17a2b8',
                                          color: 'white',
                                          border: 'none',
                                          borderRadius: '4px',
                                          cursor: 'pointer',
                                          fontSize: '12px',
                                          whiteSpace: 'nowrap'
                                        }}
                                      >
                                        Make User Admin
                                      </button>
                                    )}
                                  </>
                                )}
                              </>
                            )}

                            {/* Status toggle (Activate/Deactivate) */}
                            {u.id !== user?.id && !(u.is_superuser && !isAdmin) && (
                              <button
                                onClick={() => handleToggleUserStatus(u.id, u.status)}
                                style={{
                                  padding: '6px 12px',
                                  backgroundColor: u.status === 'active' ? '#6c757d' : '#28a745',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {u.status === 'active' ? '🚫 Deactivate' : '✓ Activate'}
                              </button>
                            )}

                            {/* Delete button: only for inactive users, not yourself, not superusers (unless you're superuser) */}
                            {u.id !== user?.id &&
                             u.status === 'inactive' &&
                             !(u.is_superuser && !isAdmin) && (
                              <button
                                onClick={() => handleDeleteUser(u.id)}
                                className="btn-danger"
                                style={{
                                  padding: '6px 12px',
                                  backgroundColor: '#dc3545',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  whiteSpace: 'nowrap'
                                }}
                                title="Only inactive users can be deleted"
                              >
                                🗑️ Delete
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Stats Section */}
        <div className="admin-section" style={{ marginTop: '40px' }}>
          <h2>Statistics</h2>
          <div className="stats-grid" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '20px',
            marginTop: '20px'
          }}>
            <div className="stat-card" style={{
              padding: '20px',
              backgroundColor: 'white',
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}>
              <h3 style={{ margin: '0 0 10px 0', color: '#666', fontSize: '14px' }}>
                Total Users
              </h3>
              <p style={{ margin: 0, fontSize: '32px', fontWeight: 'bold', color: '#333' }}>
                {users.length}
              </p>
            </div>

            <div className="stat-card" style={{
              padding: '20px',
              backgroundColor: 'white',
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}>
              <h3 style={{ margin: '0 0 10px 0', color: '#666', fontSize: '14px' }}>
                Active Users
              </h3>
              <p style={{ margin: 0, fontSize: '32px', fontWeight: 'bold', color: '#2e7d32' }}>
                {users.filter(u => u.status === 'active').length}
              </p>
            </div>

            <div className="stat-card" style={{
              padding: '20px',
              backgroundColor: 'white',
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}>
              <h3 style={{ margin: '0 0 10px 0', color: '#666', fontSize: '14px' }}>
                Verified Users
              </h3>
              <p style={{ margin: 0, fontSize: '32px', fontWeight: 'bold', color: '#1976d2' }}>
                {users.filter(u => u.is_verified).length}
              </p>
            </div>

            <div className="stat-card" style={{
              padding: '20px',
              backgroundColor: 'white',
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}>
              <h3 style={{ margin: '0 0 10px 0', color: '#666', fontSize: '14px' }}>
                Administrators
              </h3>
              <p style={{ margin: 0, fontSize: '32px', fontWeight: 'bold', color: '#f0ad4e' }}>
                {users.filter(u => u.is_superuser).length}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
